import { criterion, fail, fromCriteria, unsupported } from "@/core/check";
import type { Lesson } from "@/core/types";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { dateiendung, readHatMakros } from "@/office/word/pruefen";
import { MAKRO_MAKROS } from "../makros";

export const lektionMakros: Lesson = {
  id: "makros",
  title: "Makros aufzeichnen, ausführen und speichern",
  summary: "Wiederkehrende Handgriffe automatisieren – und wissen, wann man einem Makro trauen kann.",
  durationMinutes: 12,
  learningGoals: [
    "Ein Makro mit dem Makrorekorder aufzeichnen",
    "Makros über Alt + F8 und den VBA-Editor ausführen",
    "Dokumente mit Makros als .docm speichern und Makrosicherheit einschätzen",
  ],
  steps: [
    {
      id: "was-ist-ein-makro",
      title: "Was ein Makro ist – und die Prüfmakros dieses Kurses",
      explanation: [
        "Ein Makro ist ein kleines Programm in der Sprache VBA (Visual Basic for " +
          "Applications), das Word-Befehle ausführt. Die Prüfmakros dieses Kurses sind " +
          "solche Programme: Sie lesen Angaben aus, die das Add-in selbst nicht sehen kann, " +
          "und legen sie in der Datei ab.",
        "Makros können alles, was du in Word kannst – auch Dateien löschen oder Daten " +
          "verschicken. Deshalb blockiert Word Makros in heruntergeladenen Dateien und " +
          "zeigt bei anderen eine gelbe Sicherheitswarnung. Die Regel ist einfach: Makros nur " +
          "aktivieren, wenn die Quelle bekannt ist und man weiß, was das Makro tun soll.",
        "Die Einstellungen stehen unter „Datei“ → „Optionen“ → „Trust Center“ → " +
          "„Einstellungen für das Trust Center“ → „Makroeinstellungen“. In Firmen legt sie " +
          "meist die IT fest.",
      ],
      keyPoints: [
        "Makro = VBA-Programm, das Word-Befehle ausführt.",
        "Makros nur aus bekannter Quelle aktivieren.",
        "Trust Center → Makroeinstellungen.",
      ],
    },
    {
      id: "makro-aufzeichnen",
      title: "Ein Makro aufzeichnen",
      explanation: [
        "Der einfachste Weg zu einem eigenen Makro ist der Makrorekorder: „Ansicht“ → " +
          "„Makros“ → „Makro aufzeichnen“. Word fragt nach einem Namen und dem Speicherort – " +
          "„Alle Dokumente (Normal.dotm)“ macht das Makro überall verfügbar, der " +
          "Dokumentname nur in dieser Datei. Danach zeichnet Word jeden Befehl auf, bis du " +
          "auf „Aufzeichnung beenden“ klickst.",
        "Ein gutes erstes Makro: eine Formatvorlage zuweisen und die Absatzmarken " +
          "einblenden. Mausklicks im Text zeichnet der Rekorder nicht auf – Markieren geht " +
          "während der Aufzeichnung nur mit der Tastatur.",
        "Ausgeführt wird ein Makro über „Ansicht“ → „Makros“ (Alt + F8) → Name → " +
          "„Ausführen“. Unter „Datei“ → „Optionen“ → „Menüband anpassen“ bekommt es auf " +
          "Wunsch eine eigene Schaltfläche.",
      ],
      keyPoints: [
        "Ansicht → Makros → Makro aufzeichnen.",
        "Normal.dotm = überall verfügbar.",
        "Alt + F8 listet und startet Makros.",
      ],
      task: {
        instruction:
          "Zeichne ein Makro mit dem Namen „Kursmakro“ auf, das zum Beispiel dem aktuellen " +
          "Absatz die Formatvorlage „Überschrift 2“ zuweist.",
        hint:
          "Ansicht → Makros → Makro aufzeichnen, Name „Kursmakro“, OK, dann Strg + Alt + 2, " +
          "dann Ansicht → Makros → Aufzeichnung beenden.",
        macro: MAKRO_MAKROS,
        check: () =>
          pruefeMitMakro(MAKRO_MAKROS, (bericht) => {
            if (bericht.janein("vbZugriff") !== true) {
              return unsupported(
                "Um aufgezeichnete Makros zu finden, muss das Prüfmakro den VBA-Code lesen. " +
                  "Das erlaubt Word nur mit der Einstellung „Zugriff auf das VBA-" +
                  "Projektobjektmodell vertrauen“, die hier ausgeschaltet ist.",
                "Datei → Optionen → Trust Center → Einstellungen → Makroeinstellungen → " +
                  "Häkchen setzen und das Prüfmakro erneut ausführen. Ist die Einstellung von " +
                  "der IT gesperrt, prüfe unter Alt + F8, ob „Kursmakro“ in der Liste steht, " +
                  "und markiere den Schritt dann selbst als erledigt.",
              );
            }
            return fromCriteria(
              [criterion("Ein Makro namens „Kursmakro“ ist aufgezeichnet", bericht.janein("kursmakro") === true)],
              {
                success: `„Kursmakro“ gefunden (gespeichert in: ${bericht.text("ort") ?? "?"}).`,
                failure: "Ein Makro namens „Kursmakro“ wurde nicht gefunden.",
                hint: "Auf die Schreibweise des Namens achten und die Aufzeichnung auch wirklich beenden.",
              },
            );
          }),
      },
    },
    {
      id: "als-docm",
      title: "Dokumente mit Makros speichern",
      explanation: [
        "Eine normale Word-Datei (.docx) kann keine Makros enthalten. Wer ein Makro im " +
          "Dokument selbst speichern will, wählt beim Speichern den Dateityp „Word-Dokument " +
          "mit Makros (*.docm)“. Das m am Ende ist zugleich ein Warnsignal für alle, die die " +
          "Datei bekommen.",
        "Makros in der Normal.dotm reisen dagegen nicht mit dem Dokument – sie bleiben auf " +
          "deinem Rechner. Für ein Team gehören gemeinsame Makros in eine gemeinsame Vorlage " +
          "(.dotm).",
        "Das ist auch der Grund, warum Word beim Speichern dieses Übungsdokuments nach den " +
          "Prüfmakros fragt: Als .docx gespeichert, fallen sie weg – der Prüfbericht bleibt " +
          "erhalten, weil er kein Makro ist, sondern ein XML-Teil der Datei.",
      ],
      keyPoints: [
        ".docx = ohne Makros, .docm = mit Makros, .dotm = Vorlage mit Makros.",
        "Makros in Normal.dotm reisen nicht mit dem Dokument.",
      ],
      task: {
        instruction: "Speichere dein Übungsdokument als „Word-Dokument mit Makros (*.docm)“.",
        hint: "Datei → Speichern unter → Dateityp „Word-Dokument mit Makros (*.docm)“.",
        check: async () => {
          const endung = dateiendung();
          const hatMakros = await readHatMakros();
          if (endung === null) {
            return fail(
              "Das Dokument ist noch nicht gespeichert oder der Dateiname ist nicht lesbar.",
              "Speichere es als .docm und prüfe danach erneut.",
            );
          }
          return fromCriteria(
            [
              criterion(`Dateityp .docm (aktuell: .${endung})`, endung === "docm"),
              ...(hatMakros === null ? [] : [criterion("Das Dokument enthält ein VBA-Projekt", hatMakros)]),
            ],
            {
              success: "Gespeichert als .docm – die Makros reisen mit der Datei.",
              failure: "Das Dokument ist noch nicht als .docm gespeichert.",
              hint: "Speichern unter → Dateityp umstellen; einfach die Endung umzubenennen reicht nicht.",
            },
          );
        },
      },
    },
  ],
};
